// SettingsMyFeedbackPane.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Context
import { useUser } from 'context/UserContext';

// Services
import { listAiFeedbackByUser } from 'spaces/services/AiFeedbackService';

// Utils
import {
  aiFeedbackStatusLabel,
} from 'spaces/utils/aiFeedbackUtils';

// CSS
import './SettingsMyFeedbackPane.css';

// Components
import SettingsModalPaneHeader from './SettingsModalPaneHeader';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import Icon from 'svg/Icon';

function truncateText(value, max = 160) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function formatFeedbackDate(value) {
  if (!value) return '';
  try {
    const date = typeof value?.toDate === 'function'
      ? value.toDate()
      : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString();
  } catch {
    return '';
  }
}

function voteLabel(vote) {
  return String(vote || '').trim() === 'down' ? 'Downvoted' : 'Upvoted';
}

/**
 * SettingsMyFeedbackPane
 *
 * Lists the signed-in user's Ai upvote/downvote feedback and any admin reply.
 */
const SettingsMyFeedbackPane = React.memo(({ theme }) => {
  const { user } = useUser();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadFeedback = useCallback(async () => {
    const userKey = String(user?.key || '').trim();
    if (!userKey) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const nextRows = await listAiFeedbackByUser({ userKey });
      setRows(nextRows);
    } catch (loadError) {
      console.error('[SettingsMyFeedbackPane] failed to load feedback:', loadError);
      setError(loadError?.message || 'Failed to load your feedback.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user?.key]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  return (
    <>
      <SettingsModalPaneHeader
        title="My Feedback"
        subtitle="Upvotes and downvotes you have left on Ai replies."
      />

      <SettingsModalPaneContents>
        <div className="settings-my-feedback-list">
          {loading ? (
            <div
              className="settings-my-feedback-empty"
              style={{ color: theme.foregroundMuted }}
            >
              Loading feedback…
            </div>
          ) : null}

          {!loading && error ? (
            <div
              className="settings-my-feedback-empty"
              style={{ color: theme.foregroundMuted }}
            >
              {error}
            </div>
          ) : null}

          {!loading && !error && !rows.length ? (
            <div
              className="settings-my-feedback-empty"
              style={{ color: theme.foregroundMuted }}
            >
              You have not left any AI feedback yet.
            </div>
          ) : null}

          {!loading && !error && rows.map((row) => {
            const isDownvote = String(row.vote || '').trim() === 'down';
            const adminComment = String(row.adminComment || '').trim();
            const userComment = String(row.comment || '').trim();
            return (
              <div
                key={row.key}
                className="settings-my-feedback-item"
                style={{
                  backgroundColor: theme.backgroundMuted,
                  borderColor: theme.backgroundHover,
                }}
              >
                <div className="settings-my-feedback-item-header">
                  <div className="settings-my-feedback-item-vote">
                    <Icon
                      iconKey={isDownvote ? 'thumb_down' : 'thumb_up'}
                      width={18}
                      height={18}
                      color={theme.foreground}
                    />
                    <div
                      className="settings-my-feedback-item-vote-label"
                      style={{ color: theme.foreground }}
                    >
                      {voteLabel(row.vote)}
                    </div>
                  </div>
                  <div
                    className="settings-my-feedback-item-status"
                    style={{ color: theme.foregroundMuted }}
                  >
                    {aiFeedbackStatusLabel(row.status)}
                  </div>
                </div>

                <div
                  className="settings-my-feedback-item-prompt"
                  style={{ color: theme.foreground }}
                >
                  {truncateText(row.promptText) || '(no prompt)'}
                </div>

                {userComment ? (
                  <div
                    className="settings-my-feedback-item-note"
                    style={{ color: theme.foregroundMuted }}
                  >
                    Your comment: {userComment}
                  </div>
                ) : null}

                {adminComment ? (
                  <div
                    className="settings-my-feedback-item-admin-comment"
                    style={{
                      backgroundColor: theme.background,
                      borderColor: theme.backgroundHover,
                      color: theme.foreground,
                    }}
                  >
                    <div
                      className="settings-my-feedback-item-admin-label"
                      style={{ color: theme.foregroundMuted }}
                    >
                      Comment
                    </div>
                    <div className="settings-my-feedback-item-admin-body">
                      {adminComment}
                    </div>
                  </div>
                ) : null}

                <div
                  className="settings-my-feedback-item-date"
                  style={{ color: theme.foregroundMuted }}
                >
                  {formatFeedbackDate(row.dateCreated) || '—'}
                </div>
              </div>
            );
          })}
        </div>
      </SettingsModalPaneContents>
    </>
  );
});

SettingsMyFeedbackPane.displayName = 'SettingsMyFeedbackPane';

export default SettingsMyFeedbackPane;
